import { getPreference, setPreference } from '@fs/zion-preferences'
import { baseUrl } from '@fs/zion-config'
import { useMutation, useLazyQuery } from '@apollo/client'
import { useUser } from '@fs/zion-user'
import mutations from '../mutations'
import useGroupData from './useGroupData'
import query from '../components/groupDataHeader/queries'
import workflowStatuses from '../helpers/workflowStatuses'
import { getCurrentWorkflowActions } from '../helpers'

const MAX_REVIEWED_GROUPS_TO_STORE = 20
export const CAI_RESOLUTION = {
  VERIFIED_METADATA: 'Verified Metadata',
  UPDATED_METADATA: 'Updated Metadata',
}

export default function useSubmitCaiReview({
  resolutionType = CAI_RESOLUTION.VERIFIED_METADATA,
  onError,
  onSuccess,
  setLoading = () => {},
}) {
  const { cisId } = useUser()
  const { group } = useGroupData()
  const [fetchWorkflowActions] = useLazyQuery(query.workflowActions())

  const [doResolveCAIReview] = useMutation(mutations.resolveCAIReview())
  const [doUpdateMetisProjectStatus] = useMutation(mutations.updateMetisProjectStatus())
  const [doUpdateWorkflowActionStatus] = useMutation(mutations.updateWorkflowActionStatus())

  const submitCaiReview = async () => {
    try {
      setLoading(true)
      const caiReviewResponse = await doResolveCAIReview({
        variables: {
          resolution: resolutionType,
          groupId: group?.id,
        },
      })
      if (caiReviewResponse.data.resolveCAIReview.code === '204') {
        const currentWorkflowActions = await getCurrentWorkflowActions(fetchWorkflowActions, group)
        const parameters = currentWorkflowActions
          .find(({ name, status }) => name === 'ReviewCAI' && ['Available', 'In Progress'].includes(status))
          ?.parameters?.map(({ name, value }) => ({ name, value }))

        const metisReviewCAIResponse = await doUpdateMetisProjectStatus({
          variables: {
            updateParams: {
              actionName: 'ReviewCAI',
              newStatus: 'COMPLETED',
              projectId: group?.id,
              resourceType: 'natural',
              userId: cisId,
              parameters,
              comment: resolutionType,
            },
          },
        })
        const reviewedGroups = (await getPreference('lightyear.reviewedGroups')) || []
        if (group?.id) {
          const imageArk = window?.location?.pathname?.split('/')?.pop()
          reviewedGroups.push({
            [group.id]: {
              completedTimestamp: Date.now(),
              imageArk,
              naturalGroupId: group?.naturalGroupId,
              resolutionType,
            },
          })
          setPreference('lightyear.reviewedGroups', reviewedGroups.slice(-MAX_REVIEWED_GROUPS_TO_STORE))

          let metadataAuditActionAlreadyExists = false
          if (currentWorkflowActions?.find((action) => action?.name === workflowStatuses.METADATA_AUDIT)) {
            metadataAuditActionAlreadyExists = true
          }
          if (metadataAuditActionAlreadyExists) {
            const metisAuditResponse = await doUpdateWorkflowActionStatus({
              variables: {
                params: {
                  action: workflowStatuses.METADATA_AUDIT,
                  newStatus: 'Completed',
                  rmsId: group?.id,
                  createNewWorkflowAction: false,
                },
              },
            })
            if (!metisAuditResponse?.data?.updateMetisProjectStatus?.code === '204') {
              console.error(
                `metisAuditResponse returned code of ${metisAuditResponse?.data?.updateMetisProjectStatus?.code}`
              )
            }
          }
        }
        if (!metisReviewCAIResponse?.data?.updateMetisProjectStatus?.code === '204') {
          throw new Error(
            `metisReviewCAIResponse returned code of ${metisReviewCAIResponse?.data?.updateMetisProjectStatus?.code}`
          )
        }
        if (typeof onSuccess === 'function') await onSuccess()
        window.location.assign(`${baseUrl}/records/home/review`)
      } else {
        throw new Error(`response code ${caiReviewResponse.data.resolveCAIReview.code} received from resolveCAIReview`)
      }
    } catch (error) {
      console.error(error)
      if (typeof onError === 'function') onError(error)
    } finally {
      setLoading(false)
    }
  }

  const resetCaiReview = async () => {
    try {
      setLoading(true)
      const currentWorkflowActions = await getCurrentWorkflowActions(fetchWorkflowActions, group)
      const parameters = currentWorkflowActions
        .find(({ name, status }) => name === 'ReviewCAI' && ['In Progress'].includes(status))
        ?.parameters?.map(({ name, value }) => ({ name, value }))
      const caiResetResponse = await doUpdateMetisProjectStatus({
        variables: {
          updateParams: {
            actionName: 'ReviewCAI',
            newStatus: 'AVAILABLE',
            projectId: group.id,
            resourceType: 'natural',
            userId: cisId,
            parameters,
          },
        },
      })
      if (!caiResetResponse?.data?.updateMetisProjectStatus?.code === '204') {
        throw new Error(`caiResetResponse returned code of ${caiResetResponse?.data?.updateMetisProjectStatus?.code}`)
      }
      if (typeof onSuccess === 'function') await onSuccess()
      window.location.assign(`${baseUrl}/records/home/review`)
    } catch (error) {
      console.error(error)
      if (typeof onError === 'function') onError(error)
    } finally {
      setLoading(false)
    }
  }

  return {
    submitCaiReview,
    resetCaiReview,
  }
}
